import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Form, Modal, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { filterSeriesItems, formatSeriesLocations, sortSeriesItems } from '../../util/seriesPicker';

// Multi-select picker for the Comparison tab charts (instruments, analysts). Changes
// apply live (the parent owns the checked flags), so there is no Apply/Cancel step —
// closing the modal just reveals the already-updated chart. Sorting only affects the
// list order in the modal, never the chart's line order.
const SeriesPickerModal = ({
  show,
  onHide,
  title,
  searchPlaceholder,
  idPrefix,
  items,
  onToggle,
  onSetChecked
}) => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const filtered = sortSeriesItems(filterSeriesItems(items, search), sortBy);
  const filteredNames = filtered.map((item) => item.name);
  const noun = title.toLowerCase();

  return (
    <Modal show={show} onHide={onHide} scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          type="search"
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
        />
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => onSetChecked(filteredNames, true)}
            disabled={filteredNames.length === 0}
          >
            Select all
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => onSetChecked(filteredNames, false)}
            disabled={filteredNames.length === 0}
          >
            Clear
          </Button>
          <span className="ms-auto d-inline-flex align-items-center gap-1">
            <span className="text-muted small">Sort by</span>
            <ToggleButtonGroup
              type="radio"
              size="sm"
              name={`${idPrefix}-sort`}
              value={sortBy}
              onChange={setSortBy}
            >
              <ToggleButton id={`${idPrefix}-sort-name`} value="name" variant="outline-secondary">
                Name
              </ToggleButton>
              <ToggleButton
                id={`${idPrefix}-sort-location`}
                value="location"
                variant="outline-secondary"
              >
                Location
              </ToggleButton>
            </ToggleButtonGroup>
          </span>
        </div>
        <div className="series-picker-list">
          {filtered.map((item) => {
            const location = formatSeriesLocations(item.locations);
            return (
              <Form.Check
                key={item.name}
                type="checkbox"
                id={`${idPrefix}-${item.name}`}
                checked={!!item.checked}
                onChange={(e) => onToggle(item.name, e.target.checked)}
                label={
                  <span className="d-inline-flex align-items-center flex-wrap">
                    <span
                      className="series-picker-dot"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span className="series-picker-name">{item.name}</span>
                    {location && (
                      <>
                        {' '}
                        <span className="series-picker-location text-muted small">{location}</span>
                      </>
                    )}
                  </span>
                }
              />
            );
          })}
          {filtered.length === 0 && <div className="text-muted">{`No ${noun} match.`}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SeriesPickerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  idPrefix: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      checked: PropTypes.bool,
      color: PropTypes.string,
      locations: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSetChecked: PropTypes.func.isRequired
};

export default SeriesPickerModal;
